import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Save, RefreshCcw, Plus, Trash2, Eye, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  PHASE_STATUS,
  PHASE_ACCENT,
  STATUS_COLOR,
} from "@/lib/roadmapData";

const PHASE_STATUS_KEYS = ["complete", "in-progress", "early-development", "planned"];
const FEATURE_STATUS_KEYS = ["completed", "in-progress", "future"];
const FEATURE_STATUS_LABEL = {
  completed: "Completed",
  "in-progress": "In Progress",
  future: "Planned",
};

export default function AdminRoadmap() {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingNum, setSavingNum] = useState(null);
  const [reseeding, setReseeding] = useState(false);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/roadmap");
      setPhases(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const flash = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2500);
  };

  // Auto-recompute phase % from feature statuses.
  //   completed → 1.0  ·  in-progress → 0.5  ·  future → 0.0
  const computeAutoPct = (features) => {
    if (!features || features.length === 0) return 0;
    const score = features.reduce((acc, f) => {
      if (f.status === "completed") return acc + 1;
      if (f.status === "in-progress") return acc + 0.5;
      return acc;
    }, 0);
    return Math.round((score / features.length) * 100);
  };

  const updatePhaseLocal = (number, patch) => {
    setPhases(prev => prev.map(p => p.number === number ? { ...p, ...patch } : p));
  };

  // When a feature changes, also auto-update the phase % to match.
  // The user can still drag the slider afterwards — it just stays in sync until
  // the next feature toggle.
  const updateFeature = (phaseNum, idx, patch) => {
    setPhases(prev => prev.map(p => {
      if (p.number !== phaseNum) return p;
      const features = p.features.map((f, i) => i === idx ? { ...f, ...patch } : f);
      return { ...p, features, pct: computeAutoPct(features) };
    }));
  };

  const addFeature = (phaseNum) => {
    setPhases(prev => prev.map(p => {
      if (p.number !== phaseNum) return p;
      const features = [...p.features, { name: "New key area", status: "future" }];
      return { ...p, features, pct: computeAutoPct(features) };
    }));
  };

  const removeFeature = (phaseNum, idx) => {
    setPhases(prev => prev.map(p => {
      if (p.number !== phaseNum) return p;
      const features = p.features.filter((_, i) => i !== idx);
      return { ...p, features, pct: computeAutoPct(features) };
    }));
  };

  const recalcPhase = (phaseNum) => {
    setPhases(prev => prev.map(p => p.number === phaseNum ? { ...p, pct: computeAutoPct(p.features) } : p));
  };

  const savePhase = async (phase) => {
    setSavingNum(phase.number);
    try {
      const payload = {
        title: phase.title,
        status: phase.status,
        pct: Number(phase.pct) || 0,
        description: phase.description || "",
        goal: phase.goal || "",
        features: phase.features.map(f => ({ name: f.name, status: f.status })),
      };
      const r = await client.put(`/roadmap/phases/${phase.number}`, payload);
      setPhases(prev => prev.map(p => p.number === phase.number ? r.data : p));
      flash(`Phase ${phase.number} saved`);
    } catch (e) {
      flash(e?.response?.data?.detail || "Save failed", "err");
    } finally {
      setSavingNum(null);
    }
  };

  const reseedAll = async () => {
    if (!window.confirm("Reset the entire roadmap to factory defaults? Your edits will be lost.")) return;
    setReseeding(true);
    try {
      await client.post("/roadmap/reseed");
      await load();
      flash("Roadmap reset to defaults");
    } catch (e) {
      flash("Reset failed", "err");
    } finally {
      setReseeding(false);
    }
  };

  if (loading) {
    return <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow py-12 text-center" data-testid="admin-roadmap-loading">// loading roadmap...</div>;
  }

  return (
    <div data-testid="admin-roadmap-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase">
            Development Roadmap
          </h2>
          <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1">
            Edits go live immediately on the Home teaser and the public <code className="font-mono text-[var(--t-cyan)]">/roadmap</code> page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/roadmap" target="_blank" rel="noreferrer" className="btn-ghost !text-xs" data-testid="admin-roadmap-preview">
            <Eye className="w-3.5 h-3.5" /> Preview <ExternalLink className="w-3 h-3" />
          </Link>
          <button
            onClick={reseedAll}
            disabled={reseeding}
            className="btn-ghost !text-xs !border-[var(--t-red)] !text-[var(--t-red)] hover:!bg-[rgba(227,90,90,0.10)]"
            data-testid="admin-roadmap-reseed"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${reseeding ? "animate-spin" : ""}`} /> Reset to defaults
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`mb-4 px-4 py-2 border font-mono text-xs uppercase tracking-[0.18em] ${toast.kind === "err" ? "border-[var(--t-red)] text-[var(--t-red)] bg-[rgba(227,90,90,0.08)]" : "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"}`}
          data-testid="admin-roadmap-toast"
        >
          {toast.msg}
        </div>
      )}

      {/* Phase editors */}
      <div className="space-y-6">
        {phases.map(phase => (
          <PhaseEditor
            key={phase.number}
            phase={phase}
            saving={savingNum === phase.number}
            computeAutoPct={computeAutoPct}
            onChange={(patch) => updatePhaseLocal(phase.number, patch)}
            onFeatureChange={(idx, patch) => updateFeature(phase.number, idx, patch)}
            onAddFeature={() => addFeature(phase.number)}
            onRemoveFeature={(idx) => removeFeature(phase.number, idx)}
            onRecalc={() => recalcPhase(phase.number)}
            onSave={() => savePhase(phase)}
          />
        ))}
      </div>
    </div>
  );
}

function PhaseEditor({ phase, saving, computeAutoPct, onChange, onFeatureChange, onAddFeature, onRemoveFeature, onRecalc, onSave }) {
  const accent = PHASE_ACCENT[phase.status];
  const autoPct = computeAutoPct(phase.features);
  const isManual = phase.pct !== autoPct;

  return (
    <div
      className="panel corner-frame relative p-4 md:p-6"
      data-testid={`admin-roadmap-phase-${phase.number}`}
    >
      {/* Side stripe */}
      <span
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: `linear-gradient(to bottom, ${accent}, transparent)` }}
      />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-full font-display text-sm shrink-0"
            style={{
              backgroundColor: phase.status === "planned" ? "var(--t-bg-2)" : accent,
              color: phase.status === "planned" ? accent : "#0a1428",
              border: `2px solid ${accent}`,
              boxShadow: phase.status === "planned" ? `0 0 0 4px rgba(94,111,140,0.08)` : `0 0 0 5px ${accent}1f, 0 0 14px ${accent}80`,
            }}
          >
            {phase.number}
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--t-cyan)]">
              // Phase {String(phase.number).padStart(2, "0")}
            </div>
            <input
              value={phase.title}
              onChange={(e) => onChange({ title: e.target.value })}
              className="bg-transparent border-b border-transparent hover:border-[var(--t-border)] focus:border-[var(--t-blue)] outline-none font-display text-lg md:text-xl text-[var(--t-text)] uppercase py-0.5 w-full max-w-md"
              data-testid={`admin-phase-title-${phase.number}`}
            />
          </div>
        </div>

        <button
          onClick={onSave}
          disabled={saving}
          className="btn-primary !text-xs"
          data-testid={`admin-phase-save-${phase.number}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving" : "Save"}
        </button>
      </div>

      {/* Status + percentage row */}
      <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-5">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--t-text-dim)] mb-1.5">
            Status
          </label>
          <div className="flex flex-wrap gap-1.5" data-testid={`admin-phase-status-${phase.number}`}>
            {PHASE_STATUS_KEYS.map(k => {
              const cfg = PHASE_STATUS[k];
              const active = phase.status === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => onChange({ status: k })}
                  className="px-2.5 py-1 border text-[10px] md:text-[11px] font-mono uppercase tracking-[0.18em] transition"
                  style={{
                    background: active ? `${cfg.color}22` : "transparent",
                    borderColor: active ? cfg.color : "var(--t-border)",
                    color: active ? cfg.color : "var(--t-text-dim)",
                  }}
                >
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--t-text-dim)]">
              Progress · <span className="text-[var(--t-cyan)]">{phase.pct}%</span>
            </label>
            <div className="flex items-center gap-2">
              <span
                className={`font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border ${isManual ? "border-[var(--t-gold)] text-[var(--t-gold)]" : "border-[var(--t-blue)] text-[var(--t-cyan)]"}`}
                title={isManual ? `Auto would be ${autoPct}%. Click Recalc to snap.` : "Synced with key areas"}
                data-testid={`admin-phase-pct-mode-${phase.number}`}
              >
                {isManual ? `Manual · auto=${autoPct}%` : "Auto"}
              </span>
              {isManual && (
                <button
                  type="button"
                  onClick={onRecalc}
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--t-blue-glow)] hover:text-[var(--t-cyan)]"
                  data-testid={`admin-phase-recalc-${phase.number}`}
                >
                  <RefreshCcw className="w-3 h-3" /> Recalc
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={phase.pct}
              onChange={(e) => onChange({ pct: Number(e.target.value) })}
              className="flex-1 accent-[var(--t-blue)]"
              data-testid={`admin-phase-pct-${phase.number}`}
            />
            <input
              type="number"
              min="0"
              max="100"
              value={phase.pct}
              onChange={(e) => onChange({ pct: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
              className="w-16 bg-[var(--t-bg-2)] border border-[var(--t-border)] px-2 py-1 text-sm text-[var(--t-text)] font-mono"
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--t-text-dim)] mb-1.5">
          Short description
        </label>
        <input
          value={phase.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
          className="w-full bg-[var(--t-bg-2)] border border-[var(--t-border)] px-3 py-2 text-sm text-[var(--t-text)] focus:border-[var(--t-blue)] outline-none"
          data-testid={`admin-phase-desc-${phase.number}`}
        />
      </div>

      {/* Goal */}
      <div className="mb-5">
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--t-text-dim)] mb-1.5">
          Goal
        </label>
        <textarea
          value={phase.goal || ""}
          onChange={(e) => onChange({ goal: e.target.value })}
          rows={2}
          className="w-full bg-[var(--t-bg-2)] border border-[var(--t-border)] px-3 py-2 text-sm text-[var(--t-text)] focus:border-[var(--t-blue)] outline-none resize-y"
          data-testid={`admin-phase-goal-${phase.number}`}
        />
      </div>

      {/* Key Areas */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--t-cyan)]">
            // Key Areas ({phase.features.length})
          </label>
          <button
            type="button"
            onClick={onAddFeature}
            className="inline-flex items-center gap-1 text-xs text-[var(--t-blue-glow)] hover:text-[var(--t-cyan)] font-mono uppercase tracking-[0.15em]"
            data-testid={`admin-phase-add-feature-${phase.number}`}
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <ul className="space-y-2">
          {phase.features.map((f, idx) => (
            <li
              key={idx}
              className="flex items-center gap-2 bg-[var(--t-bg-2)] border border-[var(--t-border)] px-2 py-1.5"
              data-testid={`admin-phase-${phase.number}-feature-${idx}`}
            >
              {/* Status cycle */}
              <FeatureStatusCycle status={f.status} onChange={(s) => onFeatureChange(idx, { status: s })} />
              <input
                value={f.name}
                onChange={(e) => onFeatureChange(idx, { name: e.target.value })}
                className="flex-1 bg-transparent text-sm text-[var(--t-text)] outline-none"
              />
              <button
                type="button"
                onClick={() => onRemoveFeature(idx)}
                className="text-[var(--t-text-muted)] hover:text-[var(--t-red)] transition"
                aria-label="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
          {phase.features.length === 0 && (
            <li className="text-xs text-[var(--t-text-muted)] italic px-1">No key areas yet — click Add to create one.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

function FeatureStatusCycle({ status, onChange }) {
  const order = FEATURE_STATUS_KEYS;
  const next = () => {
    const i = order.indexOf(status);
    onChange(order[(i + 1) % order.length]);
  };
  const color = STATUS_COLOR[status];
  return (
    <button
      type="button"
      onClick={next}
      title={`Status: ${FEATURE_STATUS_LABEL[status]} — click to cycle`}
      className="shrink-0 inline-flex items-center gap-1.5 px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-[0.15em] transition"
      style={{
        borderColor: color,
        color,
        background: status === "future" ? "transparent" : `${color}1a`,
      }}
    >
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{
          backgroundColor: status === "future" ? "transparent" : color,
          border: `1.5px solid ${color}`,
          boxShadow: status === "future" ? "none" : `0 0 6px ${color}80`,
        }}
      />
      {FEATURE_STATUS_LABEL[status]}
    </button>
  );
}
